// Spreading a pasted / autofilled code across one-character OTP boxes.
//
// Lives here rather than inside AuthModal because it is the part that was
// actually wrong and the part worth testing: the boxes each held maxLength={1},
// so the browser truncated a pasted "123456" to "1" before any React handler
// ran, and pasting an OTP filled exactly one digit. Removing maxLength means
// the length rule has to be enforced here instead.

/**
 * Place the digits of `raw` into `current` starting at `start`.
 *
 * Returns a new array; `current` is untouched. Non-digits are dropped, so the
 * spaces and dashes people copy out of an SMS ("123 456", "123-456") work, as
 * does pasting a whole message when the browser hands over more than the code.
 * Anything past the end of the field is ignored rather than wrapping.
 */
export function spreadOtpDigits(
  current: string[],
  start: number,
  raw: string,
): string[] {
  if (start < 0 || start >= current.length) return current;
  const digits = raw.replace(/\D/g, "").slice(0, current.length - start);
  if (!digits) return current;

  const next = [...current];
  for (let i = 0; i < digits.length; i++) next[start + i] = digits[i];
  return next;
}

/**
 * Which box should hold focus after that paste.
 *
 * The first still-empty box, or the last box when the code is now full — so
 * Verify is one action away instead of needing a manual tab.
 */
export function otpFocusIndex(
  start: number,
  raw: string,
  length: number,
): number {
  const filled = raw.replace(/\D/g, "").slice(0, length - start).length;
  return Math.min(start + filled, length - 1);
}

/**
 * Did the user just type over a box that already had a digit?
 *
 * Without maxLength={1} a filled box accepts a second character, so onChange
 * sees "19" where the user meant to replace the 1 with a 9. That must not be
 * spread across the field — it is one digit being corrected, not a code being
 * delivered. Returns the digit the box should end up with, or null when this is
 * a genuine multi-digit delivery (paste or SMS autofill) to spread instead.
 *
 * Which character is the new one depends on where the caret was: typing at the
 * end of "1" gives "19", typing before it gives "91". Whichever end still
 * matches the old digit is the old one.
 */
export function overtypedDigit(prev: string, incoming: string): string | null {
  if (!prev || incoming.length !== 2) return null;
  return incoming[0] === prev ? incoming[1] : incoming[0];
}
